import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot, DetachedRouteHandle, PRIMARY_OUTLET, RouteReuseStrategy,
} from '@angular/router';

import { flightResultsRootRoute } from '@flight-results/flight-results-routing.constants';
import { byhoursRootRoute, hotelResultsRootRoute } from '@hotels/hotel-results/hotel-results-routing.module';
import { bookingRootRoute } from '@booking/booking-routing.module';
import { hotelDetailRootRoute } from '@hotels/hotel-detail/hotel-detail-routing.module';
import { busResultsBaseRoute } from '@buses/bus-results/constanst/bus-routes.constants';
import { activitiesHomeRootRoute } from '@activities/constants/activities.routes';
import { productResultsDetailRoute, productResultsRoute } from '@app/constants/app-routing.constants';
import { DetachedRouteHandlesService } from '@app/services/detached-route-handles.service';
import { autoHomeRootRoute } from '@autos/auto-home/constants/routes';

const autosResultsRoute = `${autoHomeRootRoute}/${productResultsRoute}`;
const autosDetailRoute = `${autoHomeRootRoute}/${productResultsDetailRoute}`;

const activitiesResultsRoute = `${activitiesHomeRootRoute}/${productResultsRoute}`;
const activitiesResultsDetailRoute = `${activitiesHomeRootRoute}/${productResultsDetailRoute}`

const REUSE_COMPONENTS = [
  flightResultsRootRoute,
  hotelResultsRootRoute,
  busResultsBaseRoute,
  byhoursRootRoute,
  autosResultsRoute,
  activitiesResultsRoute,
];

const ROOOT_ROUTES = [
  autoHomeRootRoute,
  activitiesHomeRootRoute
];

const SHOULDN_CLEAN_ROUTES = [
  flightResultsRootRoute,
  hotelResultsRootRoute,
  hotelDetailRootRoute,
  busResultsBaseRoute,
  bookingRootRoute,
  byhoursRootRoute,
  autosResultsRoute,
  autosDetailRoute,
  activitiesResultsRoute,
  activitiesResultsDetailRoute
];

const NOT_DEFINED_PARAMS = 'NOT_DEFINED_PARAMS';

@Injectable()
export class CustomReuseStrategy implements RouteReuseStrategy {

  constructor(
    private readonly detachedRouteHandlesService: DetachedRouteHandlesService,
  ) { }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.isReusableRoute(route);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const key = this.getRouteKey(route);
    if (handle) {
      this.detachedRouteHandlesService.set(route.component, key, handle);
    } else {
      this.detachedRouteHandlesService.delete(route.component, key);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (this.shouldCleanHandle(route)) {
      this.detachedRouteHandlesService.clear();
    }

    const key = this.getRouteKey(route);

    if (!this.isReusableRoute(route)) {
      return false;
    }

    return !!this.detachedRouteHandlesService.getByComponent(route.component) &&
      !!this.detachedRouteHandlesService.getByObjectString(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getRouteKey(route);
    return this.detachedRouteHandlesService.getByObjectString(key);
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    const futureKey = this.getRouteKey(future);
    const currentKey = this.getRouteKey(curr);

    const isFlightFuture = futureKey.includes(flightResultsRootRoute);
    const isFlightCurrent = currentKey.includes(flightResultsRootRoute);

    if (isFlightFuture && isFlightCurrent && futureKey !== currentKey) {
      this.detachedRouteHandlesService.storeObjectString.forEach((_, key) => {
        if (key.includes(flightResultsRootRoute)) {
          this.detachedRouteHandlesService.delete(curr.component, key)
        }
      });
      return false;
    }

    return future.routeConfig === curr.routeConfig && futureKey === currentKey;
  }

  protected shouldCleanHandle(route: ActivatedRouteSnapshot): boolean {
    const path = this.getResolvedUrlPath(route);
    return route.outlet === PRIMARY_OUTLET ? (
      SHOULDN_CLEAN_ROUTES.every(_route => !path.includes(_route) && !path.includes(NOT_DEFINED_PARAMS))
    ) || this.isFlightRouteBySector(route) : false;
  }

  protected isReusableRoute(route: ActivatedRouteSnapshot): boolean {
    const path = this.getResolvedUrlPath(route);
    return path && route.outlet === PRIMARY_OUTLET && REUSE_COMPONENTS.includes(path);
  }

  protected getRouteKey(route: ActivatedRouteSnapshot): string {
    return this.buildBaseUrl(route);
  }

  protected getResolvedUrlPath(route: ActivatedRouteSnapshot): string {
    const resolvedUrl = this.buildBaseUrl(route);
    const segments = resolvedUrl.split('/').filter(Boolean);
    if (ROOOT_ROUTES.find(r => resolvedUrl.includes(r))) {
      return `${segments[0]}/${segments[1] ?? NOT_DEFINED_PARAMS}`;
    }
    return segments[0] ?? '';
  }

  protected buildBaseUrl(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      ?.map(r => r.url.map(segment => segment.path).join('/'))
      .filter(Boolean)
      .join('/');
  }

  protected isFlightRouteBySector(route: ActivatedRouteSnapshot): boolean {
    const url = this.buildBaseUrl(route);
    const params = url.split('/');
    return params.includes(flightResultsRootRoute) && params.length === 15;
  }
}
